import { useMemo, useState, type ReactNode } from 'react'
import { Anchor, Badge, Button, Group, LoadingOverlay, Stack, Text, Title, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { Link } from '@tanstack/react-router'
import { compact } from 'lodash-es'
import { QueryBoundary } from '@/components/Common/QueryBoundary'
import { TbCameraPlus, TbCopy, TbDatabasePlus, TbDatabaseShare, TbRefresh, TbRoute, TbTrash } from 'react-icons/tb'
import PageShell from '@/components/Common/PageShell'
import type { ControlDataSchema, SyncResponse } from '@/api/main/types.gen'
import { GenericTable } from '@/components/Table/GenericTable'
import { useTable } from '@/components/Table/useTable'
import { useAuthStore } from '@/stores/auth'
import { hasPermission, PermissionGuard } from '@/utils/permissions'
import { useScrollToHash } from '@/hooks/useScrollToHash'
import { formatDateTime } from '@/utils/datetime'
import { COMPUTE_SSDR_ENABLED, MAP_SNAPSHOTS_ENABLED } from '@/constants'
import { ComputeSsdrModal } from './ComputeSsdrModal'
import { CopySchemaModal } from './CopySchemaModal'
import { CreateSchemaModal } from './CreateSchemaModal'
import { CreateSnapshotModal } from './CreateSnapshotModal'
import { SyncSnapshotModal } from './SyncSnapshotModal'
import { useMapSnapshots } from './useMapSnapshots'

const SnapshotsLoading = () => (
  <PageShell title={i18next.t('controlDataEdit', { ns: 'nav' })}>
    <LoadingOverlay visible />
  </PageShell>
)

function AnchoredTitle({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Title order={4}>
      <Anchor
        component={Link}
        to="/control-data"
        hash={id}
        underline="always"
        c="light-dark(#868e96, #c9c9c9)"
        inherit
        aria-label={`Link to #${id}`}
      >
        # {children}
      </Anchor>
    </Title>
  )
}

export default function MapSnapshotsPage() {
  return (
    <QueryBoundary
      loadingFallback={<SnapshotsLoading />}
      errorTitle={i18next.t('failedToLoadSchemas', { ns: 'controlData' })}
      errorMessage={i18next.t('unableToLoadSchemas', { ns: 'controlData' })}
      pageTitle={i18next.t('controlDataEdit', { ns: 'nav' })}
    >
      <MapSnapshotsPageInner />
    </QueryBoundary>
  )
}

function MapSnapshotsPageInner() {
  const { t } = useTranslation('mapSnapshots')
  const { t: tCd } = useTranslation('controlData')
  const {
    snapshots,
    schemas,
    createSnapshot,
    syncSnapshot,
    deleteSnapshot,
    copySchema,
    updateSchema,
    deleteSchema,
    computeSsdr,
  } = useMapSnapshots()

  // Table actions mutate data, so they require write; the snapshots table is also
  // feature-flagged, while the schemas table is always shown.
  const { userPermissions } = useAuthStore()
  const canEditSnapshots = hasPermission(userPermissions, 'map_snapshot', 'write')
  const canEditSchemas = hasPermission(userPermissions, 'control_data_edit', 'write')

  // Scroll the targeted table into view for `#snapshots` / `#schemas` deep links.
  useScrollToHash()

  // A snapshot is "in use" when a control data schema's map version matches its name
  // (e.g. "2024_07_24") — i.e. the schema was built from that snapshot. These back billing
  // periods, so they are highlighted and guarded against accidental deletion.
  const inUseVersions = useMemo(() => new Set(compact(schemas.map(s => s.map_version))), [schemas])

  const [createOpened, createCtl] = useDisclosure(false)
  const [syncOpened, syncCtl] = useDisclosure(false)
  const [createSchemaOpened, createSchemaCtl] = useDisclosure(false)
  const [copyOpened, copyCtl] = useDisclosure(false)
  const [ssdrOpened, ssdrCtl] = useDisclosure(false)
  const [syncTarget, setSyncTarget] = useState<string | null>(null)
  const [copyTarget, setCopyTarget] = useState<ControlDataSchema | null>(null)

  const onSync = (snapshot: string) => {
    setSyncTarget(snapshot)
    syncCtl.open()
  }

  const onCopySchema = (schema: ControlDataSchema) => {
    setCopyTarget(schema)
    copyCtl.open()
  }

  const confirmUpdateSchema = (schema: ControlDataSchema) => {
    modals.openConfirmModal({
      title: tCd('confirmUpdateTitle'),
      children: (
        <Stack gap="xs">
          <Text size="sm">{tCd('confirmUpdateBody', { name: schema.schema_name })}</Text>
          <Text size="sm" c="orange">
            {tCd('confirmUpdateWarning')}
          </Text>
        </Stack>
      ),
      labels: { confirm: tCd('update'), cancel: tCd('cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => updateSchema.mutate({ headers: { 'city-sense-control-data-schema': schema.schema_name } }),
    })
  }

  const confirmDeleteSchema = (schema: ControlDataSchema) => {
    modals.openConfirmModal({
      title: tCd('confirmDeleteTitle'),
      children: <Text size="sm">{tCd('confirmDeleteBody', { name: schema.schema_name })}</Text>,
      labels: { confirm: tCd('delete'), cancel: tCd('cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteSchema.mutate({ query: { schema: schema.schema_name } }),
    })
  }

  const confirmDelete = (snapshot: string) => {
    const matchingSchemas = schemas.filter(s => s.map_version === snapshot)
    const inUse = matchingSchemas.length > 0

    modals.openConfirmModal({
      title: inUse ? t('confirmDeleteInUseTitle') : t('confirmDeleteTitle'),
      children: inUse ? (
        <Stack gap="xs">
          <Text size="sm">
            {t('confirmDeleteInUseBody', {
              name: snapshot,
              schemas: matchingSchemas.map(s => s.schema_name).join(', '),
            })}
          </Text>
          <Text size="sm" c="red" fw={700}>
            {t('confirmDeleteInUseWarning')}
          </Text>
        </Stack>
      ) : (
        <Text size="sm">{t('confirmDeleteBody', { name: snapshot })}</Text>
      ),
      labels: { confirm: t('delete'), cancel: t('cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteSnapshot.mutate({ body: { snapshot_name: snapshot } }),
    })
  }

  const handleSync = async (
    snapshotName: string,
    destination: string,
    overridesFrom: string | null
  ): Promise<SyncResponse[] | undefined> => {
    return new Promise(resolve => {
      syncSnapshot.mutate(
        {
          body: {
            snapshot_from: snapshotName,
            destination_schema: destination,
            ...(overridesFrom ? { overrides_from: overridesFrom } : {}),
          },
        },
        {
          onSuccess: data => resolve(data ?? undefined),
          onError: () => resolve(undefined),
        }
      )
    })
  }

  const showSnapshots = MAP_SNAPSHOTS_ENABLED

  return (
    <PageShell
      title={t('controlDataEdit', { ns: 'nav' })}
      action={
        <Group gap="xs">
          {COMPUTE_SSDR_ENABLED && (
            <PermissionGuard userPermissions={userPermissions} resource="control_data_edit" action="write">
              <Button leftSection={<TbRoute size={16} />} onClick={ssdrCtl.open} size="xs" variant="light">
                {tCd('computeSsdr')}
              </Button>
            </PermissionGuard>
          )}
          {showSnapshots && (
            <PermissionGuard userPermissions={userPermissions} resource="map_snapshot" action="write">
              <Button
                leftSection={<TbDatabasePlus size={16} />}
                onClick={createSchemaCtl.open}
                size="xs"
                variant="light"
              >
                {t('createSchema')}
              </Button>
              <Button leftSection={<TbCameraPlus size={16} />} onClick={createCtl.open} size="xs">
                {t('createSnapshot')}
              </Button>
            </PermissionGuard>
          )}
        </Group>
      }
    >
      <Stack gap="xl">
        {showSnapshots && (
          <Stack gap="xs" id="snapshots" style={{ scrollMarginTop: 80 }}>
            <AnchoredTitle id="snapshots">{t('existingSnapshots')}</AnchoredTitle>
            <SnapshotsTable
              snapshots={snapshots}
              inUseNames={inUseVersions}
              canEdit={canEditSnapshots}
              onSync={onSync}
              onDelete={confirmDelete}
              isDeleting={deleteSnapshot.isPending}
            />
          </Stack>
        )}

        <Stack gap="xs" id="schemas" style={{ scrollMarginTop: 80 }}>
          <AnchoredTitle id="schemas">{t('controlSchemas')}</AnchoredTitle>
          <SchemasTable
            schemas={schemas}
            canEdit={canEditSchemas}
            onCopy={onCopySchema}
            onUpdate={confirmUpdateSchema}
            onDelete={confirmDeleteSchema}
            isUpdating={updateSchema.isPending}
            isDeleting={deleteSchema.isPending}
          />
        </Stack>
      </Stack>

      <CreateSnapshotModal
        opened={createOpened}
        onClose={createCtl.close}
        snapshots={snapshots}
        isSubmitting={createSnapshot.isPending}
        onSubmit={values =>
          createSnapshot.mutate(
            { body: values },
            {
              onSuccess: () => createCtl.close(),
            }
          )
        }
      />

      <SyncSnapshotModal
        opened={syncOpened}
        onClose={() => {
          syncCtl.close()
          setSyncTarget(null)
        }}
        snapshot={syncTarget}
        schemas={schemas}
        isSubmitting={syncSnapshot.isPending}
        onSync={handleSync}
      />

      <CreateSchemaModal
        opened={createSchemaOpened}
        onClose={createSchemaCtl.close}
        snapshots={snapshots}
        existingSchemaNames={schemas.map(s => s.schema_name)}
        isSubmitting={syncSnapshot.isPending}
        onSubmit={async (snapshotName, schemaName, overridesFrom) => {
          const result = await handleSync(snapshotName, schemaName, overridesFrom)
          createSchemaCtl.close()
          return result
        }}
      />

      <CopySchemaModal
        opened={copyOpened}
        onClose={() => {
          copyCtl.close()
          setCopyTarget(null)
        }}
        source={copyTarget}
        schemas={schemas}
        isSubmitting={copySchema.isPending}
        onSubmit={(sourceSchema, destinationSchema) =>
          copySchema.mutate(
            { body: { source_schema: sourceSchema, destination_schema: destinationSchema } },
            {
              onSuccess: () => {
                copyCtl.close()
                setCopyTarget(null)
              },
            }
          )
        }
      />

      <ComputeSsdrModal
        opened={ssdrOpened}
        onClose={ssdrCtl.close}
        schemas={schemas}
        isSubmitting={computeSsdr.isPending}
        onSubmit={(schema, districtId, flags) =>
          computeSsdr.mutate(
            {
              headers: { 'city-sense-control-data-schema': schema },
              query: { district_id: districtId, ...flags },
            },
            { onSuccess: () => ssdrCtl.close() }
          )
        }
      />
    </PageShell>
  )
}

function SnapshotsTable({
  snapshots,
  inUseNames,
  canEdit,
  onSync,
  onDelete,
  isDeleting,
}: {
  snapshots: string[]
  inUseNames: Set<string>
  canEdit: boolean
  onSync: (s: string) => void
  onDelete: (s: string) => void
  isDeleting: boolean
}) {
  const { t } = useTranslation('mapSnapshots')

  type Row = { snapshot_name: string; actions: string }
  const data: Row[] = snapshots.map(snapshot_name => ({ snapshot_name, actions: '' }))

  const table = useTable<Row>({
    data,
    defaultSortField: 'snapshot_name',
    defaultSortDirection: 'desc',
    itemsPerPage: 20,
    searchDebounceMs: 0,
    // canEdit-dependent key so the persisted layout matches the column set (with/without actions).
    // Both keys are fresh (the legacy 'map-snapshots-list' always had an actions column, which
    // would otherwise leak back into the read-only layout).
    tableKey: canEdit ? 'map-snapshots-table-edit' : 'map-snapshots-table',
    columns: [
      {
        key: 'snapshot_name',
        label: t('snapshotName'),
        visible: true,
        sortable: true,
        render: row => {
          const inUse = inUseNames.has(row.snapshot_name)
          return (
            <Group gap="xs" wrap="nowrap">
              <Text ff="monospace" size="sm" fw={inUse ? 700 : undefined}>
                {row.snapshot_name}
              </Text>
              {inUse && (
                <Tooltip label={t('inUseTooltip')} withArrow>
                  <Badge color="yellow" size="xs" variant="light">
                    {t('inUse')}
                  </Badge>
                </Tooltip>
              )}
            </Group>
          )
        },
      },
      ...(canEdit
        ? [
            {
              key: 'actions' as const,
              label: t('actions'),
              visible: true,
              sortable: false,
              align: 'right' as const,
              render: (row: Row) => (
                <Group gap="xs" justify="flex-end">
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<TbDatabaseShare size={14} />}
                    onClick={() => onSync(row.snapshot_name)}
                  >
                    {t('sync')}
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    leftSection={<TbTrash size={14} />}
                    onClick={() => onDelete(row.snapshot_name)}
                    loading={isDeleting}
                  >
                    {t('delete')}
                  </Button>
                </Group>
              ),
            },
          ]
        : []),
    ],
  })

  return (
    <GenericTable
      {...table.tableProps}
      getRowKey={row => row.snapshot_name}
      emptyMessage={t('noSnapshots')}
      csvFileName="map-snapshots.csv"
    />
  )
}

function SchemasTable({
  schemas,
  canEdit,
  onCopy,
  onUpdate,
  onDelete,
  isUpdating,
  isDeleting,
}: {
  schemas: ControlDataSchema[]
  canEdit: boolean
  onCopy: (s: ControlDataSchema) => void
  onUpdate: (s: ControlDataSchema) => void
  onDelete: (s: ControlDataSchema) => void
  isUpdating: boolean
  isDeleting: boolean
}) {
  const { t } = useTranslation('controlData')

  const mono = (value: string) => (
    <Text ff="monospace" size="sm">
      {value}
    </Text>
  )

  type Row = ControlDataSchema & { actions: string }
  const data: Row[] = schemas.map(s => ({ ...s, actions: '' }))

  const table = useTable<Row>({
    data,
    defaultSortField: 'schema_name',
    defaultSortDirection: 'desc',
    defaultFilterField: 'schema_name',
    itemsPerPage: 20,
    searchDebounceMs: 0,
    // Key depends on canEdit: the persisted column layout differs (actions column), and a
    // fresh key avoids inheriting the old read-only table's stored layout that lacked actions.
    tableKey: canEdit ? 'map-snapshots-schemas-edit' : 'map-snapshots-schemas',
    columns: [
      {
        key: 'schema_name',
        label: t('schemaName'),
        visible: true,
        sortable: true,
        render: row => mono(row.schema_name),
      },
      {
        key: 'map_version',
        label: t('mapVersion'),
        visible: true,
        sortable: true,
        render: row => mono(row.map_version ?? ''),
      },
      {
        key: 'modified_at',
        label: t('modifiedAt'),
        visible: true,
        sortable: true,
        render: row => <Text size="sm">{formatDateTime(row.modified_at)}</Text>,
      },
      ...(canEdit
        ? [
            {
              key: 'actions' as const,
              label: t('actions'),
              visible: true,
              sortable: false,
              align: 'right' as const,
              render: (row: Row) => (
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <Button size="xs" variant="light" leftSection={<TbCopy size={14} />} onClick={() => onCopy(row)}>
                    {t('copy')}
                  </Button>
                  <Button
                    size="xs"
                    color="orange"
                    variant="light"
                    leftSection={<TbRefresh size={14} />}
                    onClick={() => onUpdate(row)}
                    loading={isUpdating}
                  >
                    {t('update')}
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    leftSection={<TbTrash size={14} />}
                    onClick={() => onDelete(row)}
                    loading={isDeleting}
                  >
                    {t('delete')}
                  </Button>
                </Group>
              ),
            },
          ]
        : []),
    ],
  })

  return (
    <GenericTable
      {...table.tableProps}
      getRowKey={row => row.schema_name}
      emptyMessage={t('noSchemas')}
      csvFileName="control-data-schemas.csv"
    />
  )
}
